/**
 * useStamina — optimistic client clock + refetch on focus/visibility.
 *
 * Approach (consensus plan §2.5.1):
 *  1. On mount, call /v1/me to fetch the canonical stamina state + serverTime.
 *  2. Compute clientToServerSkew = serverNow - clientNow. We use this to drive
 *     the local countdown clock from a synthesized "server now" so it matches
 *     what the API will see when /start is called.
 *  3. Tick a 1s interval. When the local serverNow >= nextRegenAt, optimistically
 *     bump `current` by 1 (capped at 20) and advance nextRegenAt by 30 minutes.
 *  4. On `visibilitychange` and `focus`, refetch /v1/me so the optimistic state
 *     can be reconciled with the authoritative server view (handles backgrounded
 *     tabs that miss interval ticks, and post-/start atomic-409 recovery).
 *
 * INVARIANT: the regen interval is 30 minutes, the cap is 20. These match the
 * server constants. If the server changes either, fetch /me to resync.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { MeResponse, StaminaState } from '@sagwa/types'
import { apiClient } from '../api/client'

export const STAMINA_MAX = 20
const REGEN_INTERVAL_MS = 30 * 60 * 1000

export type UseStaminaResult = {
  stamina: StaminaState | null
  todayBest: number | null
  serverTime: Date | null
  loading: boolean
  error: Error | null
  refetch: () => Promise<void>
  /** Seconds remaining until the next regen tick. 0 when at cap or unknown. */
  countdownToNextRegen: number
}

export function useStamina(): UseStaminaResult {
  const [stamina, setStamina] = useState<StaminaState | null>(null)
  const [todayBest, setTodayBest] = useState<number | null>(null)
  const [serverTime, setServerTime] = useState<Date | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [countdownToNextRegen, setCountdown] = useState(0)

  // Skew = serverNow - clientNow at the moment /me responded.
  // serverNow() = Date.now() + skewRef.current
  const skewRef = useRef(0)
  const mountedRef = useRef(true)

  const applyMe = useCallback((me: MeResponse): void => {
    if (!mountedRef.current) return
    const serverNow = new Date(me.serverTime).getTime()
    skewRef.current = serverNow - Date.now()
    setStamina(me.stamina)
    setTodayBest(me.todayBest)
    setServerTime(new Date(me.serverTime))
    setError(null)
  }, [])

  const refetch = useCallback(async (): Promise<void> => {
    try {
      const me = await apiClient.getMe()
      applyMe(me)
    } catch (err) {
      if (!mountedRef.current) return
      setError(err instanceof Error ? err : new Error(String(err)))
    }
  }, [applyMe])

  // Initial mount: fetch /me.
  useEffect(() => {
    mountedRef.current = true
    setLoading(true)
    void refetch().finally(() => {
      if (mountedRef.current) setLoading(false)
    })
    return () => {
      mountedRef.current = false
    }
  }, [refetch])

  // Tick the local countdown + optimistic regen.
  useEffect(() => {
    const tick = (): void => {
      setStamina((cur) => {
        if (!cur) {
          setCountdown(0)
          return cur
        }
        if (cur.current >= STAMINA_MAX || !cur.nextRegenAt) {
          setCountdown(0)
          return cur
        }
        const nextRegenMs = new Date(cur.nextRegenAt).getTime()
        const serverNowMs = Date.now() + skewRef.current
        const remainMs = nextRegenMs - serverNowMs
        if (remainMs > 0) {
          setCountdown(Math.ceil(remainMs / 1000))
          return cur
        }
        // Optimistic regen tick.
        const nextCurrent = Math.min(STAMINA_MAX, cur.current + 1)
        const advancedNextRegenAt =
          nextCurrent >= STAMINA_MAX
            ? null
            : new Date(nextRegenMs + REGEN_INTERVAL_MS).toISOString()
        setCountdown(advancedNextRegenAt ? REGEN_INTERVAL_MS / 1000 : 0)
        return {
          ...cur,
          current: nextCurrent,
          lastRegenAt: new Date(nextRegenMs).toISOString(),
          nextRegenAt: advancedNextRegenAt,
        }
      })
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [])

  // Refetch on visibility + focus.
  useEffect(() => {
    const onVisibility = (): void => {
      if (!document.hidden) void refetch()
    }
    const onFocus = (): void => {
      void refetch()
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onFocus)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onFocus)
    }
  }, [refetch])

  return {
    stamina,
    todayBest,
    serverTime,
    loading,
    error,
    refetch,
    countdownToNextRegen,
  }
}
